"use client";

import { useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { Search, Filter } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import {
  JOURNAL_MAX_ENTRIES,
  useJournalStore,
} from "@/vybe-trader-client/messaging/stores/useJournalStore";
import { JournalVirtualizedLog } from "@/components/journal/JournalVirtualizedLog";
import {
  useJournalFilteredEntries,
  useJournalMessageTypes,
} from "@/components/journal/useJournalFilteredEntries";

interface JournalScreenProps {
  onBack: () => void;
}

export default function JournalScreen({ onBack }: JournalScreenProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const entries = useJournalStore((s) => s.entries);
  const filteredEntries = useJournalFilteredEntries(
    entries,
    searchQuery,
    selectedTypes
  );
  const actionTypes = useJournalMessageTypes(entries);

  const toggleSelectedType = (type: string) => {
    setSelectedTypes((current) =>
      current.includes(type)
        ? current.filter((selectedType) => selectedType !== type)
        : [...current, type]
    );
  };

  const clearSelectedTypes = () => {
    setSelectedTypes([]);
  };

  const emptyMessage =
    searchQuery || selectedTypes.length > 0
      ? "No entries match your filters"
      : "No journal entries yet";

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground">
      <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-8 w-8 shrink-0"
        >
          <FaChevronLeft className="h-4 w-4 text-blue-400" />
        </Button>
        <span className="text-sm font-semibold">Journal</span>
        <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
          {filteredEntries.length} / {JOURNAL_MAX_ENTRIES}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search journal…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0">
              <Filter className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-64 w-56 overflow-y-auto">
            <DropdownMenuLabel>Message types</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={clearSelectedTypes}>
              Show all
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {actionTypes.map((type) => (
              <DropdownMenuCheckboxItem
                key={type}
                checked={selectedTypes.includes(type)}
                onCheckedChange={() => toggleSelectedType(type)}
                onSelect={(event) => event.preventDefault()}
              >
                {type}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <JournalVirtualizedLog
        entries={filteredEntries}
        emptyMessage={emptyMessage}
        className="min-h-0 flex-1"
      />
    </div>
  );
}
